import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { isType } from '@sorare/core/src/lib/gql';

import { AnnouncementNotification } from './AnnouncementNotification';
import { AuctionNotification } from './AuctionNotification';
import { AuctionReminderNotification } from './AuctionReminderNotification';
import { AutoClaimNotification } from './AutoClaimNotification';
import { CardCollectionNotification } from './CardCollectionNotification';
import { CardNotification } from './CardNotification';
import { ConversionCreditNotification } from './ConversionCreditNotification';
import { ExternalDepositNotification } from './ExternalDepositNotification';
import { InGameCurrencyNotification } from './InGameCurrencyNotification';
import { KycRequestNotification } from './KycRequestNotification';
import { LoanAvailableNotification } from './LoanAvailableNotification';
import { MarketingNotification } from './MarketingNotification';
import { MissedLineupNotification } from './MissedLineupNotification';
import { OfferNotification } from './OfferNotification';
import { ReferralRewardNotification } from './ReferralRewardNotification';
import { SaleNotification } from './SaleNotification';
import { So5LineupNotification } from './So5LineupNotification';
import { TaskNotification } from './TaskNotification';
import { UserRelationNotification } from './UserRelationNotification';
import type { Notification_notification } from './__generated__/index.graphql';
import type { CommonNotificationProps } from './types';

type Props = CommonNotificationProps & {
  notification: Notification_notification;
};

export const Notification = ({ notification, ...rest }: Props) => {
  if (isType(notification, 'AuctionNotification')) {
    return <AuctionNotification notification={notification} {...rest} />;
  }
  if (isType(notification, 'SaleNotification')) {
    return <SaleNotification notification={notification} {...rest} />;
  }
  if (isType(notification, 'OfferNotification')) {
    return <OfferNotification notification={notification} {...rest} />;
  }
  if (isType(notification, 'So5LineupNotification')) {
    return <So5LineupNotification notification={notification} {...rest} />;
  }
  if (isType(notification, 'AnnouncementNotification')) {
    return <AnnouncementNotification notification={notification} {...rest} />;
  }
  if (isType(notification, 'ReferralRewardNotification')) {
    return <ReferralRewardNotification notification={notification} {...rest} />;
  }
  if (isType(notification, 'CardNotification')) {
    return <CardNotification notification={notification} {...rest} />;
  }
  if (isType(notification, 'CardCollectionNotification')) {
    return <CardCollectionNotification notification={notification} {...rest} />;
  }
  if (isType(notification, 'ExternalDepositNotification')) {
    return (
      <ExternalDepositNotification notification={notification} {...rest} />
    );
  }
  if (isType(notification, 'KycRequestNotification')) {
    return <KycRequestNotification notification={notification} {...rest} />;
  }
  if (isType(notification, 'UserRelationNotification')) {
    return <UserRelationNotification notification={notification} {...rest} />;
  }
  if (isType(notification, 'ReminderNotification')) {
    return (
      <AuctionReminderNotification notification={notification} {...rest} />
    );
  }
  if (isType(notification, 'MarketingNotification')) {
    return <MarketingNotification notification={notification} />;
  }
  if (isType(notification, 'MissedLineupNotification')) {
    return <MissedLineupNotification notification={notification} />;
  }
  if (isType(notification, 'AutoClaimNotification')) {
    return <AutoClaimNotification notification={notification} />;
  }
  if (isType(notification, 'InGameCurrencyNotification')) {
    return <InGameCurrencyNotification notification={notification} />;
  }
  if (isType(notification, 'LoanNotification')) {
    return <LoanAvailableNotification notification={notification} />;
  }
  if (isType(notification, 'TaskNotification')) {
    return <TaskNotification notification={notification} />;
  }
  if (isType(notification, 'ConversionCreditNotification')) {
    return <ConversionCreditNotification notification={notification} />;
  }
  return null;
};

Notification.fragments = {
  notification: gql`
    fragment Notification_notification on NotificationInterface {
      id
      ...AuctionNotification_auctionNotification
      ...SaleNotification_saleNotification
      ...OfferNotification_offerNotification
      ...So5LineupNotification_so5LineupNotification
      ...AnnouncementNotification_announcementNotification
      ...ReferralRewardNotification_referralRewardNotification
      ...CardNotification_cardNotification
      ...CardCollectionNotification_cardCollectionNotification
      ...ExternalDepositNotification_externalDepositNotification
      ...KycRequestNotification_kycRequestNotification
      ...UserRelationNotification_userRelationNotification
      ...ReminderNotification_auctionReminderNotification
      ...MarketingNotification_marketingNotification
      ...MissedLineupNotification_missedLineupNotification
      ...AutoClaimNotification_autoClaimNotification
      ...InGameCurrencyNotification_inGameCurrencyNotification
      ...LoanAvailableNotification_loanNotification
      ...TaskNotification_taskNotification
      ...ConversionCreditNotification_ConversionCreditNotification
    }
    ${AuctionNotification.fragments.auctionNotification}
    ${SaleNotification.fragments.saleNotification}
    ${OfferNotification.fragments.offerNotification}
    ${So5LineupNotification.fragments.so5LineupNotification}
    ${AnnouncementNotification.fragments.announcementNotification}
    ${ReferralRewardNotification.fragments.referralRewardNotification}
    ${CardNotification.fragments.cardNotification}
    ${CardCollectionNotification.fragments.cardCollectionNotification}
    ${ExternalDepositNotification.fragments.externalDepositNotification}
    ${KycRequestNotification.fragments.kycRequestNotification}
    ${UserRelationNotification.fragments.userRelationNotification}
    ${AuctionReminderNotification.fragments.auctionReminderNotification}
    ${MarketingNotification.fragments.marketingNotification}
    ${MissedLineupNotification.fragments.missedLineupNotification}
    ${AutoClaimNotification.fragments.autoClaimNotification}
    ${InGameCurrencyNotification.fragments.inGameCurrencyNotification}
    ${LoanAvailableNotification.fragments.loanNotification}
    ${TaskNotification.fragments.taskNotification}
    ${ConversionCreditNotification.fragments.ConversionCreditNotification}
  ` as TypedDocumentNode<Notification_notification>,
};
